import { Loader, MultiSelect, Select } from "@mantine/core"
import { useContext, useEffect, useRef, useState, useCallback, useMemo } from "react";
import { NBAInsightsContext } from "../../context/global-state";
import { useLeague } from "../../context/LeagueContext";
import { LEAGUE } from "../../models/NBA";

import { useForm, Controller } from "react-hook-form"
import { getGameString } from "../../utils/utils";
import { getGameActivePlayers, getGames } from "../../api/Games";
import { Game, Player, Season, SeasonType } from "../../client";

import { filterPlayersWithVideos, GameSearchData, VideoSearchRanges } from "../../models/SearchData";
import { SeasonTypeSchema, StatSchema } from "../../client/schemas.gen";
import SearchFormActions from "./SearchFormActions";

interface GameSearchFormProps {
  submitText: string,
  newSearchText?: string,
  lastSearch: GameSearchData | null,
  onSubmit: (formData: GameSearchData) => void,
  onAddToStack?: (formData: GameSearchData) => void,
  onNewSearch?: (formData: GameSearchData) => void,
  isStackMode?: boolean,
  isStackFull?: boolean,
  isEditing?: boolean,
  onUpdateInStack?: (formData: GameSearchData) => void,
  onCancelEdit?: () => void,
}

const fields = ["singleSeason", "seasonType", "game", "players", "periods", "statCategories"]

const GameSearchForm: React.FC<GameSearchFormProps> = ({
  submitText,
  newSearchText,
  lastSearch,
  onSubmit,
  onAddToStack,
  onNewSearch,
  isStackMode = false,
  isStackFull = false,
  isEditing = false,
  onUpdateInStack,
  onCancelEdit,
}) => {
  const { games, setGames, nbaPlayers, wnbaPlayers } = useContext(NBAInsightsContext)
  const { league } = useLeague()
  const [gamesLoading, setGamesLoading] = useState<boolean>(false)
  const [gamePlayers, setGamePlayers] = useState<Player[]>([])
  const [gamePlayersLoading, setGamePlayersLoading] = useState<boolean>(false)

  const currentLeaguePlayers = useMemo(() => {
    const raw = league === LEAGUE.NBA ? nbaPlayers : wnbaPlayers
    return filterPlayersWithVideos(league, raw)
  }, [league, nbaPlayers, wnbaPlayers])

  const defaultSeasonType: SeasonType = "Regular Season"

  const gamesRef = useRef(games)
  gamesRef.current = games

  const getDefaultValues = useCallback((): GameSearchData => {
    if (lastSearch && lastSearch.league === league) {
      return lastSearch
    }
    return {
      league,
      game: gamesRef.current[0] as Game,
      seasonType: defaultSeasonType,
      singleSeason: VideoSearchRanges[league][defaultSeasonType][0],
      categories: [],
      players: [],
      periods: [],
    }
  }, [lastSearch, league])

  const { handleSubmit, control, watch, setValue, reset } = useForm<GameSearchData>({
    defaultValues: getDefaultValues(),
  })

  const form = watch()

  const formGameRef = useRef(form.game)
  formGameRef.current = form.game

  const lastSearchRef = useRef(lastSearch)
  lastSearchRef.current = lastSearch

  const setGamesRef = useRef(setGames)
  setGamesRef.current = setGames

  useEffect(() => {
    reset(getDefaultValues())
  }, [getDefaultValues, reset])

  useEffect(() => {
    if (!form.singleSeason || !form.seasonType) return
    setGamesLoading(true)
    ;(async () => {
      const updatedGames = await getGames(
        league,
        form.singleSeason,
        form.seasonType,
        undefined,
        undefined
      )
      setGamesRef.current(updatedGames)
      const currentGame = formGameRef.current
      if (updatedGames.length > 0 && (!currentGame || !updatedGames.some(g => g.id === currentGame.id))) {
        const pinnedLastSearch = lastSearchRef.current
        const targetGame = (pinnedLastSearch?.game?.id && updatedGames.find(g => g.id === pinnedLastSearch.game.id)) || updatedGames[0]
        setValue("game", targetGame)
      }
      setGamesLoading(false)
    })()
  }, [league, form.singleSeason, form.seasonType, setValue])

  useEffect(() => {
    const gameId = form.game?.id
    if (!gameId) {
      setGamePlayers([])
      return
    }

    setGamePlayersLoading(true)
    ;(async () => {
      try {
        const roster = await getGameActivePlayers(gameId, currentLeaguePlayers)
        setGamePlayers(roster.length > 0 ? roster : currentLeaguePlayers)
      } catch {
        setGamePlayers(currentLeaguePlayers)
      } finally {
        setGamePlayersLoading(false)
      }
    })()
  }, [form.game?.id, currentLeaguePlayers])

  const prevGameIdRef = useRef(form.game?.id)
  useEffect(() => {
    if (prevGameIdRef.current !== form.game?.id) {
      prevGameIdRef.current = form.game?.id
      if (lastSearchRef.current?.game?.id === form.game?.id && lastSearchRef.current?.players) {
        setValue("players", lastSearchRef.current.players)
      } else {
        setValue("players", [])
      }
    }
  }, [form.game?.id, setValue])

  const gameComponent = (
    <Controller
      name="game"
      control={control}
      render={({field}) => (
        <Select
          {...field}
          styles={{
            root: {
              padding: 10,
              paddingTop: 5,
              paddingBottom: 0,
            },
            input: {
              borderRadius: 10
            }
          }}
          data-testid={"game"}
          label="Select game"
          onChange={(value) => {
            field.onChange(games.find(game => game.id === value))
          }}
          rightSection={gamesLoading ? <Loader size="xs" /> : null}
          value={field.value?.id || (games[0] ? games[0].id : null)}
          data={games.map(game => ({ value: game.id, label: getGameString(game) }))}
          allowDeselect={false}
          searchable
        />
      )}
    />
  )

  const seasonTypeComponent = (
    <Controller
      name="seasonType"
      control={control}
      render={({field}) => (
        <Select 
          {...field}
          styles={{
            root: {
              padding: 10,
              paddingTop: 5,
              paddingBottom: 0,
            },
            input: {
              borderRadius: 10
            }
          }}
          data-testid={"seasonType"}
          label="Season Type"
          data={Object.values(SeasonTypeSchema.enum)}
          allowDeselect={false}
          onChange={(value) => {
            if (value) field.onChange(value)
          }}
        />
      )}
    />
  )

  const singleSeasonComponent = (
    <Controller
      name="singleSeason"
      control={control}
      render={({field}) => (
        <Select 
          {...field}
          styles={{
            root: {
              padding: 10,
              paddingTop: 5,
              paddingBottom: 0,
            },
            input: {
              borderRadius: 10
            }
          }}
          data-testid={"singleSeason"}
          label="Season"
          data={VideoSearchRanges[league][form.seasonType || "Regular Season"] as Season[]}
          value={field.value}
          allowDeselect={false}
          onChange={(value) => {
            if (value) field.onChange(value)
          }}
        />
      )}
    />
  )


  const playersComponent = (
    <Controller
      name="players"
      control={control}
      render={({ field }) => (
        <MultiSelect
          {...field}
          styles={{
            root: {
              padding: 10,
              paddingTop: 5,
              paddingBottom: 0,
            },
            input: {
              borderRadius: 10,
            },
          }}
          data-testid={"players"}
          label="Filter by players"
          placeholder={
            gamePlayersLoading
              ? "Loading players..."
              : field.value && field.value.length > 0
              ? "Add more..."
              : "All players"
          }
          rightSection={gamePlayersLoading ? <Loader size="xs" /> : null}
          data={gamePlayers.map((player) => player.fullName)}
          value={(field.value || []).map((p) => p.fullName)}
          onChange={(selectedNames) => {
            const selectedPlayers = selectedNames
              .map((name) => gamePlayers.find((p) => p.fullName === name))
              .filter((p): p is Player => Boolean(p));
            field.onChange(selectedPlayers);
          }}
          searchable
          clearable
        />
      )}
    />
  )

  const categoriesComponent = (
    <Controller
      name="categories"
      control={control}
      render={({field}) => (
        <MultiSelect 
          {...field}
          styles={{
            root: {
              padding: 10,
              paddingTop: 5,
              paddingBottom: 0,
            },
            input: {
              borderRadius: 10
            }
          }}
          data-testid={"categories"}
          label="Stat categories"
          placeholder="Add a stat category"
          data={Object.values(StatSchema.enum)}
          value={field.value || []}
          onChange={(value) => field.onChange(value)}
          searchable
        />
      )}
    />
  )

  const periodOptions = [
    { value: "1", label: "Q1" },
    { value: "2", label: "Q2" },
    { value: "3", label: "Q3" },
    { value: "4", label: "Q4" },
    { value: "5", label: "OT" },
  ]

  const periodsComponent = (
    <Controller
      name="periods"
      control={control}
      render={({ field }) => (
        <MultiSelect
          {...field}
          styles={{
            root: {
              padding: 10,
              paddingTop: 5,
              paddingBottom: 0,
            },
            input: {
              borderRadius: 10,
            },
          }}
          data-testid={"periods"}
          label="Quarter / Period"
          placeholder={field.value && field.value.length > 0 ? undefined : "All Quarters"}
          data={periodOptions}
          value={(field.value || []).map(String)}
          onChange={(values) => {
            field.onChange(values.map(Number))
          }}
          clearable
        />
      )}
    />
  )

  const components: { [key: string]: JSX.Element; } = {
    "singleSeason": singleSeasonComponent,
    "seasonType": seasonTypeComponent,
    "game": gameComponent,
    "players": playersComponent,
    "periods": periodsComponent,
    "statCategories": categoriesComponent,
  }

  return (
    <>
      <form id="search-filter-form" onSubmit={handleSubmit(onSubmit)}>
        {fields.map((field: string, idx) => <div key={idx}>{components[field]}</div>)}

        <SearchFormActions
          submitText={submitText}
          newSearchText={newSearchText}
          isStackMode={isStackMode}
          isStackFull={isStackFull}
          isEditing={isEditing}
          disabled={gamesLoading || !form.game}
          onAddToStack={onAddToStack ? handleSubmit(onAddToStack) : undefined}
          onUpdateInStack={handleSubmit(onUpdateInStack || onSubmit)}
          onCancelEdit={onCancelEdit}
          onNewSearch={handleSubmit(onNewSearch || onSubmit)}
        />
      </form>
    </>
  )
}

export default GameSearchForm